import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AutoScroller } from '@dnd-kit/dom';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from '@gouvfr-lasuite/cunningham-react';
import { Icon } from '@gouvfr-lasuite/ui-kit';
import useCardDndHandlers from '../../hooks/use-card-dnd-handlers'; // ─── C2 : hook factorisé ───

import CardContainer from '../../containers/CardContainer';
import CardModalContainer from '../../containers/CardModalContainer';
import ListContainer from '../../containers/ListContainer';
import SortableList from './List/SortableList';
import ListCreate from './ListCreate';
import HierarchyView from './HierarchyView';
import styles from './Board.module.scss';

function Board({
  listIds,
  boardId,
  isCardModalOpened,
  canEdit,
  viewMode, // ─── vue ───
  lists, // ─── couloirs ───
  layout, // ─── couloirs ───
  parentNames, // ─── C2 ───
  onListCreate,
  onListMove,
  onCardMove,
  onCardTransfer,
}) {
  const [t] = useTranslation();
  const [isListAddOpened, setIsListAddOpened] = useState(false);

  // const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'hierarchy'

  // const handleToggleView = useCallback(() => {
  //   setViewMode((prev) => (prev === 'kanban' ? 'hierarchy' : 'kanban'));
  // }, []);

  const handleAddListClick = useCallback(() => {
    setIsListAddOpened(true);
  }, []);

  const handleAddListClose = useCallback(() => {
    setIsListAddOpened(false);
  }, []);

  // track the card's original parent DOM node so we can revert the
  // OptimisticSortingPlugin's cross-container DOM move before React reconciles
  // otherwise it was throwing: `Failed to execute 'removeChild' on 'Node'`
  // and managing `event.preventDefault()` as in https://github.com/clauderic/dnd-kit/issues/1747 was not ideal
  // since managing all the state manually

  /**
   * @type {NonNullable<
   *   import("react").ComponentProps<
   *     typeof import("@dnd-kit/react").DragDropProvider
   *   >["plugins"]
   * >}
   */
  const providerPlugins = useCallback(
    (defaults) => [
      ...defaults,
      AutoScroller.configure({
        // it avoids having the scrollbar jumps when dragging on the cards container border
        layoutShiftCompensation: false,
      }),
    ],
    [],
  );

  // ─── C2 : handlers DnD factorisés (Kanban : avec onListMove) ───
  const { handleDragStart, handleDragEnd } = useCardDndHandlers({
    boardId,
    onCardTransfer,
    onCardMove,
    onListMove, // ← Board gère le tri des listes
  });

  useEffect(() => {
    if (isListAddOpened) {
      window.scroll(document.body.scrollWidth, 0);
    }
  }, [listIds, isListAddOpened]);

  return (
    <div className={styles.wrapper}>
      {viewMode === 'hierarchy' ? (
        <HierarchyView
          lists={lists}
          layout={layout}
          parentNames={parentNames}
          boardId={boardId}
          onCardMove={onCardMove}
          onCardTransfer={onCardTransfer}
        />
      ) : (
        <DragDropProvider
          plugins={providerPlugins}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={styles.container}>
            {listIds.map((listId, listIndex) => (
              <SortableList key={listId} id={listId} index={listIndex} canEdit={canEdit} />
            ))}
            {canEdit && (
              <div className={styles.list}>
                {isListAddOpened ? (
                  <ListCreate onCreate={onListCreate} onClose={handleAddListClose} />
                ) : (
                  <Button
                    color="brand"
                    variant="tertiary"
                    size="small"
                    onClick={handleAddListClick}
                    className={styles.addListButton}
                  >
                    <Icon name="add" />
                    <span>
                      {listIds.length > 0 ? t('action.addAnotherList') : t('action.addList')}
                    </span>
                  </Button>
                )}
              </div>
            )}
          </div>

          <DragOverlay>
            {(source) => {
              if (source.type === 'List') {
                return (
                  <div className={classNames(styles.dragOverlay, styles.list)}>
                    <ListContainer id={source.id} index={source.index} />
                  </div>
                );
              }

              if (source.type === 'Card') {
                return (
                  <div className={styles.card}>
                    <CardContainer id={source.id} />
                  </div>
                );
              }

              return null;
            }}
          </DragOverlay>
        </DragDropProvider>
      )}
      {isCardModalOpened && <CardModalContainer />}
    </div>
  );
}

Board.propTypes = {
  listIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  boardId: PropTypes.string.isRequired,
  isCardModalOpened: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  viewMode: PropTypes.string.isRequired, // ─── vue ───
  lists: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  layout: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  parentNames: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onListCreate: PropTypes.func.isRequired,
  onListMove: PropTypes.func.isRequired,
  onCardMove: PropTypes.func.isRequired,
  onCardTransfer: PropTypes.func.isRequired,
};

Board.defaultProps = {
  layout: null,
  parentNames: {}, // ─── C2 ───
};

export default React.memo(Board);
